import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  ApiVersion,
  EMPTY_GLOBAL_CONFIG,
  GLOBAL_CONFIG_ID,
  GlobalService,
  IGlobalConfig
} from '@dadohr/shared/domainTypes/globalConfig';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { capitalize, snakeCase, sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import {
  reshowAnnouncement,
  saveAnnouncement,
  useAnnouncement
} from '../../../services/announcement';
import { Loader } from '../../../components/Loader';
import { CanvasBar } from '../../../layout/Canvas';
import { Page } from '../../../layout/Page';
import { Section } from '../../../layout/Section';
import {
  LoadingValue,
  store,
  useMappedLoadingValue
} from '../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';

const toConfigDoc = generateToDocFn<IGlobalConfig>();

const configDocRef = () =>
  store().collection(FS.globalConfig).doc(GLOBAL_CONFIG_ID);

const cache = createDocumentListenerGetter<IGlobalConfig>(
  () => configDocRef(),
  toConfigDoc,
  EMPTY_GLOBAL_CONFIG
);

export const useGlobalConfig = (): LoadingValue<IGlobalConfig> => {
  return useMappedLoadingValue(useDocumentListener(cache('')), (d) => {
    const defaults = EMPTY_GLOBAL_CONFIG();
    if (!d) {
      return defaults;
    }
    return {
      ...defaults,
      ...d.data,
      services: {
        ...defaults.services,
        ...d.data.services
      },
      integrationVersions: {
        ...defaults.integrationVersions,
        ...(d.data.integrationVersions || {})
      },
      ai: d.data.ai
    };
  });
};

export const enableService = (key: GlobalService, value: boolean) => {
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const config = await t.get(ref);
    const d = (config.data() as IGlobalConfig) || EMPTY_GLOBAL_CONFIG();
    return t.set(ref, {
      ...d,
      services: {
        ...d.services,
        [key]: value
      }
    });
  });
};

export const setIntegrationVersion = (integration: keyof IGlobalConfig['integrationVersions'], version: ApiVersion) => {
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const config = await t.get(ref);
    const d = (config.data() as IGlobalConfig) || EMPTY_GLOBAL_CONFIG();
    return t.set(ref, {
      ...d,
      integrationVersions: {
        ...d.integrationVersions,
        [integration]: version
      }
    });
  });
};

const Services = ({ config }: { config: IGlobalConfig }) => {
  const options: {
    key: GlobalService;
    label: string;
    checked: boolean;
  }[] = Object.entries(config.services).map(([key, checked]) => ({
    key: key as GlobalService,
    checked,
    label: snakeCase(key).split('_').map(capitalize).join(' ')
  }));

  return (
    <Section>
      <CanvasBar>Services</CanvasBar>
      <Card>
        <CardContent>
          <Table>
            <TableBody>
              {sortBy(options, (o) => o.key).map((o) => (
                <TableRow key={o.key}>
                  <TableCell>{o.label}</TableCell>
                  <TableCell align="right">
                    <Switch
                      checked={o.checked}
                      onChange={(ev) => enableService(o.key, ev.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};

const IntegrationVersions = ({ config }: { config: IGlobalConfig }) => {
  const options = Object.entries(config.integrationVersions).map(([key, version]) => ({
    key: key as keyof IGlobalConfig['integrationVersions'],
    version,
    label: snakeCase(key).split('_').map(capitalize).join(' ')
  }));

  return (
    <Section>
      <CanvasBar>Feature Versions</CanvasBar>
      <Card>
        <CardContent>
          <Table>
            <TableBody>
              {sortBy(options, (o) => o.key).map((o) => (
                <TableRow key={o.key}>
                  <TableCell>{o.label}</TableCell>
                  <TableCell align="right">
                    <ToggleButtonGroup
                      value={o.version}
                      exclusive
                      onChange={(_, newVersion) => newVersion && setIntegrationVersion(o.key, newVersion)}
                      size="small"
                    >
                      <ToggleButton value="v1">v1</ToggleButton>
                      <ToggleButton value="v2">v2</ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};

const FeatureAnnouncement = () => {
  const [current] = useAnnouncement();
  const [videoUrl, setVideoUrl] = useState('');
  const [title, setTitle] = useState('');
  const [active, setActive] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sync local fields whenever the stored announcement changes (e.g. after a save or
  // another admin's edit). Keyed on version so typing isn't clobbered mid-edit.
  useEffect(() => {
    setVideoUrl(current?.videoUrl ?? '');
    setTitle(current?.title ?? '');
    setActive(current?.active ?? false);
    // Sync only when the stored version changes — depending on the individual fields would
    // clobber the admin's in-progress edits on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.version]);

  const onSave = async () => {
    setSaving(true);
    try {
      await saveAnnouncement({ videoUrl, title, active });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section>
      <CanvasBar>Feature Announcement</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            One-time video modal shown to all dashboard admins. Saving bumps the version so
            it shows once to everyone; "Re-show to everyone" re-shows the current video
            without changing it. Current version: {current?.version ?? 0}
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Video URL (YouTube / Vimeo / Loom)"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || !videoUrl.trim()}
              onClick={onSave}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              disabled={saving || !current?.videoUrl}
              onClick={() => reshowAnnouncement()}
            >
              Re-show to everyone
            </Button>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
};

export const PageGlobalConfig = () => {
  const [config] = useGlobalConfig();
  return (
    <Page>
      {!config && <Loader />}
      {config && (
        <>
          <Services config={config} />
          <IntegrationVersions config={config} />
          <FeatureAnnouncement />
        </>
      )}
    </Page>
  );
};
